import React from "react"
import { Col, Row, Table } from "react-bootstrap"

import FormikBootstrap from "@/components/formik-bootstrap"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import Pricing from "@/pricing"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"
import { ReferencedValueTypes } from "@/onboarding/features/product/models/Product"
import RateInputs from "@/onboarding/features/product/components/shared/RateInputs"
import ComponentOperatorSelect from "@/onboarding/features/product/components/shared/ComponentOperatorSelect"

import OperatorDisplay from "./OperatorDisplay"
import TenderStatus from "./TenderStatus"

const WriteView = ({
  hideRates = false,
  showOperator = false,
  referencedValueMessage = "",
  tender = null,
}): JSX.Element => (
  <Row>
    <Col md={8} className="d-flex flex-column gap-2">
      <h5 className="mb-0">
        Edit Details
        <TenderStatus tender={tender} />
      </h5>
      <Row>
        <Col md={3}>
          {/*
            The name field is required.
            There is an open feature request for extracting requiredness from the schema: https://github.com/jaredpalmer/formik/issues/1241
          */}
          <FormikBootstrap.TextInput name="name" label="Name *" />
        </Col>
        <Col md={3}>
          <ReferencedValueSelect
            name="entertainmentType"
            type={ReferencedValueTypes.ENTERTAINMENT_TYPE}
            label="Type"
            emptyMessage={referencedValueMessage}
          />
        </Col>
        <Col md={3}>
          <FormikBootstrap.NumberInput name="duration" label="Duration (Minutes)" />
        </Col>
      </Row>
      <Row>
        <Col md={9}>
          <FormikBootstrap.TextInput name="description" label="Description" />
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <FormikBootstrap.NumberInput name="capacity" label="Capacity" />
        </Col>
        <Col md={showOperator ? 3 : 6}>
          <FormikBootstrap.TextInput name="website" label="Website" />
        </Col>
        {showOperator && (
          <Col md={3}>
            <ComponentOperatorSelect />
          </Col>
        )}
      </Row>
    </Col>
    {!hideRates && (
      <Col md={4} className="ms-n4">
        <h5 className="mb-0">Edit Rates</h5>
        <RateInputs name="ratesheet" />
      </Col>
    )}
  </Row>
)

const ReadView = ({
  component,
  hideRates,
  showOperator,
  tender,
  previousValues = false,
}): JSX.Element => {
  const textStyle = previousValues ? "original-product-text" : ""

  return (
    <div className="d-flex flex-column gap-2">
      <h5 className={textStyle}>
        Details
        <TenderStatus tender={tender} />
      </h5>
      <Table>
        <thead>
          <tr>
            <th className={textStyle}>Name</th>
            <th className={textStyle}>Type</th>
            <th className={textStyle}>Duration (Minutes)</th>
            <th className={textStyle}>Website</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td aria-label="Name">
              <TextDisplay value={component.name} previousValue={previousValues} />
            </td>
            <td aria-label="Entertainment Type">
              <TextDisplay value={component.entertainmentType} previousValue={previousValues} />
            </td>
            <td aria-label="Duration">
              <TextDisplay value={component.duration} previousValue={previousValues} />
            </td>
            <td aria-label="Website">
              <TextDisplay value={component.website} previousValue={previousValues} />
            </td>
          </tr>
        </tbody>
        <thead>
          <tr>
            <th className={textStyle}>Capacity</th>
            {showOperator && <th className={textStyle}>Operator</th>}
            <th className={textStyle} colSpan={showOperator ? 2 : 3}>
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td aria-label="Capacity">
              <TextDisplay value={component.capacity} previousValue={previousValues} />
            </td>
            {showOperator && (
              <td aria-label="Operator">
                <OperatorDisplay operatorId={component.operatorId} previousValue={previousValues} />
              </td>
            )}
            <td aria-label="Description" colSpan={showOperator ? 2 : 3}>
              <TextDisplay value={component.description} previousValue={previousValues} />
            </td>
          </tr>
        </tbody>
      </Table>
      {!hideRates && (
        <Col md={3}>
          <h5 className={textStyle}>Rates</h5>
          <Pricing.View value={component.ratesheet} previousValues={previousValues} />
        </Col>
      )}
    </div>
  )
}

const View = ({
  component,
  previousValues = false,
  editing = false,
  hideRates = false,
  showOperator = false,
  tender = null,
  referencedValueMessage = "",
}): JSX.Element =>
  editing ? (
    <WriteView
      hideRates={hideRates}
      showOperator={showOperator}
      referencedValueMessage={referencedValueMessage}
      tender={tender}
    />
  ) : (
    <ReadView
      component={component}
      hideRates={hideRates}
      showOperator={showOperator}
      previousValues={previousValues}
      tender={tender}
    />
  )

export default View
